import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { inrAxis, inr, pct } from "../lib/format";

export const PALETTE = ["#10B981", "#2DD4BF", "#EAB308", "#38BDF8", "#A78BFA", "#F59E0B", "#F472B6", "#94A3B8"];

const axisStyle = { fontSize: 11, fill: "#8B9299", fontFamily: "Manrope" };

const GlassTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const change = payload[0]?.payload?._change;
  const changePct = payload[0]?.payload?._changePct;
  return (
    <div className="rounded-lg border border-white/10 bg-[#0A110D]/95 backdrop-blur-xl px-3.5 py-2.5 shadow-[0_8px_32px_rgba(0,0,0,0.6)] min-w-[160px]">
      {label && <p className="text-[11px] text-zinc-500 mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-semibold tnum" style={{ color: p.color || p.fill || "#34D399" }}>
          {p.name}: {inr(p.value)}
        </p>
      ))}
      {change !== undefined && change !== null && (
        <p className={`text-[11px] tnum mt-1 font-semibold ${change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
          {change >= 0 ? "+" : "−"}{inr(Math.abs(change))} ({change >= 0 ? "+" : ""}{changePct}%)
          <span className="text-zinc-600 font-normal"> vs prev period</span>
        </p>
      )}
    </div>
  );
};

const withChange = (data, dataKey) =>
  data.map((d, i) => {
    if (i === 0 || !data[i - 1][dataKey]) return { ...d, _change: null, _changePct: null };
    const prev = data[i - 1][dataKey];
    const change = d[dataKey] - prev;
    return { ...d, _change: change, _changePct: prev ? ((change / prev) * 100).toFixed(2) : null };
  });

export function GrowthArea({ data, dataKey = "value", xKey = "label", height = 300, testId,
  investedKey = null, showChange = true, connectNulls = true }) {
  const series = showChange ? withChange(data, dataKey) : data;
  return (
    <div data-testid={testId} style={{ width: "100%", height, minHeight: height }}>
      <ResponsiveContainer>
        <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gEmerald" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2DD4BF" stopOpacity={0.34} />
              <stop offset="55%" stopColor="#10B981" stopOpacity={0.12} />
              <stop offset="100%" stopColor="#10B981" stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
          <XAxis dataKey={xKey} tick={axisStyle} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.08)" }} minTickGap={40} />
          <YAxis tick={axisStyle} tickLine={false} axisLine={false} tickFormatter={(v) => inrAxis(v)} width={64} />
          <Tooltip content={<GlassTooltip />} />
          {investedKey && (
            <Line type="monotone" dataKey={investedKey} name="Invested Value" stroke="#EAB308"
              strokeWidth={1.5} strokeDasharray="5 4" dot={false} strokeOpacity={0.75} />
          )}
          {investedKey && <Legend iconType="plainline" wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />}
          <Area type="monotone" dataKey={dataKey} name="Portfolio Value" stroke="#2DD4BF" strokeWidth={2}
            fill="url(#gEmerald)" dot={false} activeDot={{ r: 4, fill: "#2DD4BF", stroke: "#030706" }}
            connectNulls={connectNulls} animationDuration={700} animationEasing="ease-out" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function AllocationDonut({ data, height = 280, testId }) {
  return (
    <div data-testid={testId} style={{ width: "100%", height, minHeight: height }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="category" innerRadius="60%" outerRadius="88%"
            paddingAngle={2} strokeWidth={0}>
            {data.map((_, i) => (
              <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
            ))}
          </Pie>
          <Tooltip content={<GlassTooltip />} />
          <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 12, fontFamily: "Manrope", color: "#A1A1AA" }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export function FlowBars({ data, height = 280, testId }) {
  return (
    <div data-testid={testId} style={{ width: "100%", height, minHeight: height }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
          <XAxis dataKey="month" tick={axisStyle} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.08)" }} />
          <YAxis tick={axisStyle} tickLine={false} axisLine={false} tickFormatter={(v) => inrAxis(v)} width={64} />
          <Tooltip content={<GlassTooltip />} />
          <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 12, color: "#A1A1AA" }} />
          <Bar dataKey="inflow" name="Inflows" fill="#10B981" radius={[3, 3, 0, 0]} maxBarSize={20} />
          <Bar dataKey="outflow" name="Outflows" fill="#EAB308" fillOpacity={0.75} radius={[3, 3, 0, 0]} maxBarSize={20} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
